import { FC, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Toggle } from "components/inputs/Toggle"

import { settingsService } from "services/settings/settings.service"

import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

interface CurrenciesTabProps {
  currencies: Record<string, boolean>
  onChange: (currencies: Record<string, boolean>) => void
  refreshSettings: () => Promise<void>
  onSaved?: () => void
}

export const CurrenciesTab: FC<CurrenciesTabProps> = ({ currencies, onChange, refreshSettings, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  const handleToggle = (code: string) => {
    onChange({ ...currencies, [code]: !currencies[code] })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const success = await settingsService.updateRCoin(currencies)
      if (success) {
        toast.success(t("settings.currencies.toastUpdated") as string)
        onSaved?.()
        setHistoryKey((key) => key + 1)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleResetDone = async () => {
    await refreshSettings()
    setHistoryKey((key) => key + 1)
  }

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h3 className="text-xl font-semibold mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.tabs.currencies")}</h3>
        <SettingsHistoryNote target="rcoin" refreshKey={historyKey} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 rounded-lg border border-black/20 dark:border-white/10 bg-white dark:bg-transparent">
        {Object.entries(currencies).map(([code, enabled]) => (
          <div
            key={code}
            className="flex items-center justify-between gap-4 border border-black/10 dark:border-white/10 py-4 px-2 odd:bg-black/[0.03] dark:odd:bg-white/[0.04]"
          >
            <span className="text-base font-semibold text-gray-800 dark:text-white">{code}</span>
            <Toggle checked={enabled} onChange={() => handleToggle(code)} disabled={saving} />
          </div>
        ))}
        <div className="hidden md:flex items-center justify-between gap-4 border border-black/10 dark:border-white/10 bg-white dark:bg-white/[0.04] py-4 px-2" />
      </div>

      <SettingsSectionActions
        onSave={handleSave}
        saving={saving}
        resetTarget="rcoin"
        resetLabel={t("settings.tabs.currencies")}
        onResetDone={handleResetDone}
      />
    </section>
  )
}
